import { Injectable, Injector, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { UserRoles } from 'src/app/pages/enums/UserRoles';
import { IamUiPolicyService } from 'src/app/pages/user-management/services/iam-ui-policy.service';

export type UserType = UserModel | undefined;

function getWindow(): any {
  return window;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private unsubscribe: Subscription[] = [];
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  isExpired = false;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private injector: Injector
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    // Session restore is owned by APP_INITIALIZER (single getUserByToken call).
    // Do not also subscribe here — parallel /user calls raced with AuthGuard and wiped JWTs.
  }

  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: AuthModel) => this.setAuthFromLocalStorage(auth)),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        const apiMessages = err?.error?.messageList;
        if (Array.isArray(apiMessages) && apiMessages.length) {
          console.error(apiMessages[0]);
        }
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout(): void {
    localStorage.removeItem('ga_user_id');
    localStorage.clear();
    this.currentUserSubject.next(undefined);
    try {
      this.injector.get(IamUiPolicyService).clear();
    } catch {
      // IAM policy may be unavailable during early bootstrap
    }
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserType> {
    if (this.currentUserSubject.value) {
      return of(this.currentUserSubject.value);
    }

    const auth = this.getAuthFromLocalStorage();

    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.authToken).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
          localStorage.setItem('ga_user_id', String((user as any).id));
          (window as any).dataLayer = (window as any).dataLayer || [];
          (window as any).dataLayer.push({ 'user-id': (user as any).id });
        }
        // Do not logout() here — AuthGuard / interceptor decide.
        // logout() inside this map raced with APP_INITIALIZER and cleared valid sessions.
        return user;
      }),
      catchError(() => of(undefined)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  getUsers(): Observable<UserModel[]> {
    return this.authHttpService.getUsers();
  }

  getUsersCountries(linkedAccount: string): Observable<UserModel[]> {
    return this.authHttpService.getUsersCountries(linkedAccount);
  }

  registration(user: UserModel): Observable<any> {
    return this.authHttpService.createUser(user).pipe(
      map((createdUser: UserModel) => createdUser)
    );
  }

  delete(userId: any): Observable<any> {
    return this.authHttpService.deleteUser(userId);
  }

  update(user: UserModel): Observable<any> {
    return this.authHttpService.updateUser(user).pipe(
      map((updatedUser: UserModel) => updatedUser)
    );
  }

  chekIfCurrUserHasExpectedRole(expectedRoles?: UserRoles[] | undefined): boolean {
    if (expectedRoles == undefined) {
      return true;
    }
    const currentUserRole = this.currentUserValue?.role;
    if (!currentUserRole) {
      return false;
    }
    const roleName =
      typeof currentUserRole === 'string' ? currentUserRole : currentUserRole.name;
    return expectedRoles.some((expectedRole) => roleName === expectedRole);
  }

  forgotPassword(email: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  updatePassword(email: string, password: string, token: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.updatePassword(email, password, token);
  }

  checkTokenExpiration(token: string): Observable<any> {
    return this.authHttpService.checkTokenExpiration(token);
  }

  getTokenMasterfile(email: string) {
    return this.authHttpService.getTokenMasterfile(email);
  }

  get nativeWindow() {
    return getWindow();
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    if (auth && auth.authToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }
      return JSON.parse(lsValue);
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }
}
